import {useSelector} from 'react-redux';
import {getVisibleSubTabKeys} from 'redux/store/auth';

/**
 * Filters a tab's sub-tab config down to what the current admin may see.
 *
 * @param {string} tabName  The NavTabsEnum value of the parent tab (matches the
 *                          `tab` key on an activeAdminTab grant + the catalog).
 * @param {Array<{key: string}>} subTabs  Full sub-tab config (each needs a
 *                          `key` that matches the catalog sub-tab key).
 * @returns {{
 *   visibleSubTabs: Array,        // subTabs filtered to the granted keys
 *   visibleKeys: string[],        // the granted keys, in config order
 *   isVisible: (key: string) => boolean,
 *   firstVisible: object | null,  // first visible sub-tab (for default landing)
 * }}
 *
 * Resolution (see getVisibleSubTabKeys): SUPERADMIN/ADMIN → all; grant
 * allSubTabs → all; explicit subTabs → only those; legacy grant → all; no
 * grant for the tab → none.
 */
export const useVisibleSubTabs = (tabName, subTabs) => {
  const activeAdminTab = useSelector(
    state => state?.auth?.userData?.user?.activeAdminTab,
  );

  const allKeys = subTabs.map(sub => sub.key);
  const visibleKeys = getVisibleSubTabKeys(activeAdminTab, tabName, allKeys);
  const visibleSubTabs = subTabs.filter(sub => visibleKeys.includes(sub.key));

  return {
    visibleSubTabs,
    visibleKeys,
    isVisible: key => visibleKeys.includes(key),
    firstVisible: visibleSubTabs[0] || null,
  };
};
